import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Avatar,
  Paper,
  Divider,
  IconButton,
  Chip,
  FormControlLabel,
  Checkbox,
  TextField,
  Alert,
  Snackbar,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Event as DateIcon,
  ContentCut as ServiceIcon,
  Person as MasterIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  NavigateBefore as PrevMonthIcon,
  NavigateNext as NextMonthIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/ru";

const getApiBase = () => {
  const envBase = process.env.REACT_APP_API_URL;
  if (!envBase) return "/api";

  // If an absolute URL was provided (http/https), prefer a relative path to
  // avoid Mixed Content issues and always follow the page scheme.
  if (/^https?:\/\//i.test(envBase)) {
    try {
      const u = new URL(envBase);
      return u.pathname || "/api";
    } catch {
      // fall through
    }
  }

  if (
    envBase.startsWith("http://") &&
    typeof window !== "undefined" &&
    window.location.protocol === "https:"
  ) {
    return envBase.replace(/^http:\/\//, "https://");
  }
  return envBase;
};

const API_BASE = getApiBase();
const UPLOADS_BASE = process.env.REACT_APP_UPLOADS_URL || "/uploads";

const normalizePhone = (value: string) => {
  const digits = (value || "").replace(/\D/g, "");
  if (!digits) return "";

  if (digits.length === 11 && (digits.startsWith("8") || digits.startsWith("7"))) {
    return `+7${digits.slice(1)}`;
  }

  if (digits.length === 10) {
    return `+7${digits}`;
  }

  return `+${digits}`;
};

// Устанавливаем русскую локаль
dayjs.locale("ru");

interface Location {
  id: number;
  name: string;
  address: string;
  work_hours: {
    monday: { enabled: boolean; start: string; end: string };
    tuesday: { enabled: boolean; start: string; end: string };
    wednesday: { enabled: boolean; start: string; end: string };
    thursday: { enabled: boolean; start: string; end: string };
    friday: { enabled: boolean; start: string; end: string };
    saturday: { enabled: boolean; start: string; end: string };
    sunday: { enabled: boolean; start: string; end: string };
  };
}

interface ServiceCategory {
  id: number;
  name: string;
}

interface ServiceSubcategory {
  id: number;
  category_id: number;
  name: string;
  description?: string;
  is_active?: boolean;
}

interface Service {
  id: number;
  name: string;
  description: string;
  base_price: number;
  duration_minutes: number;
  category_id: number;
  subcategory_id?: number | null;
  allows_parallel?: boolean;
  apply_minutes?: number;
  finish_minutes?: number;
  is_insertable?: boolean;
}

interface Master {
  id: number;
  full_name: string;
  phone: string;
  email?: string;
  role: string;
  is_active: boolean;
  photo_url?: string;
  rating?: number;
  working_hours?: any;
  specialization_category_ids?: number[];
}

interface TimeSlot {
  time: string;
  available: boolean;
  master_id?: number;
  isBookedStart?: boolean;
  bookedClient?: { name: string; phone: string };
}

interface BookingData {
  locationId: number;
  date: Dayjs;
  serviceId: number;
  masterId: number | null; // null для "любой мастер"
  time: string;
  clientName: string;
  clientPhone: string;
  clientEmail?: string;
  comment?: string;
}

export default function BookingNewPage() {
  const navigate = useNavigate();
  const userRole = localStorage.getItem("user_role") || "client";
  const isAdmin = userRole === "admin";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [masters, setMasters] = useState<Master[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [slotsRefreshVersion, setSlotsRefreshVersion] = useState(0);
  const [occupiedInfoOpen, setOccupiedInfoOpen] = useState(false);
  const [occupiedInfo, setOccupiedInfo] = useState<{ time: string; name: string; phone: string } | null>(null);

  const [currentUserInfo, setCurrentUserInfo] = useState<{ full_name?: string; phone?: string } | null>(null);
  const [clientLookup, setClientLookup] = useState<
    | { status: "idle"; normalizedPhone: string; message?: string }
    | { status: "loading"; normalizedPhone: string; message?: string }
    | { status: "found"; normalizedPhone: string; client: { id: number; name: string; phone: string } }
    | { status: "not_found"; normalizedPhone: string; message?: string }
    | { status: "error"; normalizedPhone: string; message?: string }
  >({ status: "idle", normalizedPhone: "" });
  
  const [bookingData, setBookingData] = useState<BookingData>({
    locationId: 0,
    date: dayjs(),
    serviceId: 0,
    masterId: null,
    time: "",
    clientName: "",
    clientPhone: "",
    clientEmail: "",
    comment: "",
  });

  useEffect(() => {
    if (!isAdmin) return;

    const token = localStorage.getItem("token");
    const normalized = normalizePhone(bookingData.clientPhone);

    if (!normalized) {
      setClientLookup({ status: "idle", normalizedPhone: "" });
      return;
    }

    const digits = normalized.replace(/\D/g, "");
    if (digits.length < 11) {
      setClientLookup({ status: "idle", normalizedPhone: normalized });
      return;
    }

    const timer = window.setTimeout(async () => {
      setClientLookup({ status: "loading", normalizedPhone: normalized });
      try {
        const resp = await fetch(`${API_BASE}/clients/by-phone/${encodeURIComponent(normalized)}`, {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : undefined,
        });

        if (resp.ok) {
          const client = await resp.json();
          setClientLookup({ status: "found", normalizedPhone: normalized, client });
          setBookingData((prev) => ({
            ...prev,
            clientName: (client?.name || "").trim() || prev.clientName,
          }));
          return;
        }

        if (resp.status === 404) {
          setClientLookup({ status: "not_found", normalizedPhone: normalized });
          return;
        }

        let text = "";
        try {
          text = await resp.text();
        } catch {
          text = "";
        }
        setClientLookup({ status: "error", normalizedPhone: normalized, message: text || `HTTP ${resp.status}` });
      } catch (e) {
        setClientLookup({
          status: "error",
          normalizedPhone: normalized,
          message: e instanceof Error ? e.message : String(e),
        });
      }
    }, 400);

    return () => window.clearTimeout(timer);
  }, [isAdmin, bookingData.clientPhone]);

  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [subcategories, setSubcategories] = useState<ServiceSubcategory[]>([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState<number | null>(null);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [anyMaster, setAnyMaster] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [alert, setAlert] = useState({ show: false, message: "", severity: "success" as "success" | "error" });
  const [shouldSkipLocationStep, setShouldSkipLocationStep] = useState(false);

  // Динамические шаги в зависимости от количества мест
  const steps = shouldSkipLocationStep 
    ? [
        "Выбор даты", 
        "Выбор услуги",
        "Выбор мастера",
        "Выбор времени",
        "Подтверждение"
      ]
    : [
        "Выбор места",
        "Выбор даты", 
        "Выбор услуги",
        "Выбор мастера",
        "Выбор времени",
        "Подтверждение"
      ];

  // Загрузка данных
  useEffect(() => {
    loadInitialData();
    loadMasters();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    void (async () => {
      try {
        const resp = await fetch(`${API_BASE}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) return;
        const data = await resp.json();
        const full_name = data?.full_name ? String(data.full_name) : "";
        const phone = data?.phone ? String(data.phone) : "";
        setCurrentUserInfo({ full_name, phone });
        if (full_name) {
          localStorage.setItem("user_name", full_name);
        }
      } catch {
        // ignore
      }
    })();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      console.log('Loading initial data for booking...');
      
      const token = localStorage.getItem("token");
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : undefined;
      
      // Загружаем реальные места
      const locationsResponse = await fetch(`${API_BASE}/locations/`, {
        headers: authHeaders,
      });
      
      // Загружаем категории услуг
      const categoriesResponse = await fetch(`${API_BASE}/services/public/categories`, {
        headers: authHeaders,
      });
      
      let locationsData: Location[] = [];
      let categoriesData: ServiceCategory[] = [];
      
      if (locationsResponse.ok) {
        const locationsResult = await locationsResponse.json();
        const locationsArray = locationsResult.locations || locationsResult;
        console.log('Raw locations data:', locationsArray);
        locationsData = locationsArray.map((loc: any) => {
          const mappedLoc = {
            id: loc.id,
            name: loc.name,
            address: loc.physical_address || loc.legal_address || 'Адрес не указан',
            work_hours: {
            monday: { 
              enabled: loc.working_hours?.monday?.works || false, 
              start: loc.working_hours?.monday?.open || "09:00", 
              end: loc.working_hours?.monday?.close || "18:00" 
            },
            tuesday: { 
              enabled: loc.working_hours?.tuesday?.works || false, 
              start: loc.working_hours?.tuesday?.open || "09:00", 
              end: loc.working_hours?.tuesday?.close || "18:00" 
            },
            wednesday: { 
              enabled: loc.working_hours?.wednesday?.works || false, 
              start: loc.working_hours?.wednesday?.open || "09:00", 
              end: loc.working_hours?.wednesday?.close || "18:00" 
            },
            thursday: { 
              enabled: loc.working_hours?.thursday?.works || false, 
              start: loc.working_hours?.thursday?.open || "09:00", 
              end: loc.working_hours?.thursday?.close || "18:00" 
            },
            friday: { 
              enabled: loc.working_hours?.friday?.works || false, 
              start: loc.working_hours?.friday?.open || "09:00", 
              end: loc.working_hours?.friday?.close || "18:00" 
            },
            saturday: { 
              enabled: loc.working_hours?.saturday?.works || false, 
              start: loc.working_hours?.saturday?.open || "10:00", 
              end: loc.working_hours?.saturday?.close || "16:00" 
            },
            sunday: {
              enabled: loc.working_hours?.sunday?.works || false,
              start: loc.working_hours?.sunday?.open || "10:00",
              end: loc.working_hours?.sunday?.close || "16:00",
            },
            },
          };
          console.log('Mapped location:', mappedLoc);
          return mappedLoc;
        });
        console.log('Final locations data:', locationsData);
      }
      
      if (categoriesResponse.ok) {
        const categoriesResult = await categoriesResponse.json();
        categoriesData = categoriesResult.categories;
        console.log('Categories loaded:', categoriesData);
      } else {
        console.log('Categories response not ok:', categoriesResponse.status);
      }
      
      setLocations(locationsData);
      setCategories(categoriesData);
      
      console.log('Locations loaded:', locationsData.length);
      
      // Если только одно место, пропускаем шаг выбора места
      if (locationsData.length === 1) {
        console.log('Skipping location step - only one location');
        setBookingData(prev => ({ ...prev, locationId: locationsData[0].id }));
        setShouldSkipLocationStep(true);
      } else {
        console.log('Showing location step - multiple locations');
        setShouldSkipLocationStep(false);
      }
      
    } catch (error) {
      console.error("Error loading initial data:", error);
      setAlert({ show: true, message: "Ошибка загрузки данных", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async (categoryId: number, subcategoryId: number | null) => {
    try {
      setServicesLoading(true);
      const token = localStorage.getItem("token");
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : undefined;
      const qs = subcategoryId ? `?subcategory_id=${encodeURIComponent(String(subcategoryId))}` : "";
      const response = await fetch(`${API_BASE}/services/public/categories/${categoryId}/services${qs}`, {
        headers: authHeaders,
      });
      
      if (response.ok) {
        const data = await response.json();
        const servicesData: Service[] = data.services.map((service: any) => ({
          id: service.id,
          name: service.name,
          description: service.description,
          base_price: service.base_price,
          duration_minutes: service.duration_minutes,
          category_id: service.category_id,
          subcategory_id: service.subcategory_id,
          allows_parallel: Boolean(service.allows_parallel),
          apply_minutes: Number(service.apply_minutes || 0),
          finish_minutes: Number(service.finish_minutes || 0),
          is_insertable: Boolean(service.is_insertable),
        }));
        setServices(servicesData);
      } else {
        setServices([]);
      }
    } catch (error) {
      console.error("Error loading services:", error);
      setServices([]);
    } finally {
      setServicesLoading(false);
    }
  };

  const loadSubcategories = async (categoryId: number) => {
    try {
      const token = localStorage.getItem("token");
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : undefined;
      const response = await fetch(`${API_BASE}/services/public/categories/${categoryId}/subcategories`, {
        headers: authHeaders,
      });

      if (response.ok) {
        const data = await response.json();
        const subcats: ServiceSubcategory[] = (data?.subcategories || []).map((s: any) => ({
          id: s.id,
          category_id: s.category_id,
          name: s.name,
          description: s.description,
          is_active: s.is_active,
        }));
        setSubcategories(subcats);
      } else {
        setSubcategories([]);
      }
    } catch (error) {
      console.error("Error loading subcategories:", error);
      setSubcategories([]);
    }
  };

  const loadMasters = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/users/masters`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      
      if (response.ok) {
        const data = await response.json();
        const mastersData: Master[] = data.map((master: any) => ({
          id: master.id,
          full_name: master.full_name,
          phone: master.phone,
          email: master.email || null,
          role: master.role,
          is_active: master.is_active,
          photo_url: master.photo_url || null,
          rating: master.rating || null,
          working_hours: master.working_hours || null,
          specialization_category_ids: master.specialization_category_ids || [],
        }));
        setMasters(mastersData);
        console.log('Masters loaded:', mastersData);
      } else {
        console.log('Masters response not ok:', response.status);
        setMasters([]);
      }

      console.log('Initial data loaded successfully');

    } catch (error) {
      console.error("Error loading initial data:", error);
      setAlert({ show: true, message: "Ошибка загрузки данных", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleLocationSelect = (locationId: number) => {
    setBookingData({ ...bookingData, locationId });
    handleNext();
  };

  const handleDateSelect = (date: Dayjs) => {
    setBookingData({ ...bookingData, date });
    handleNext();
  };

  const handleServiceSelect = (serviceId: number) => {
    setBookingData({ ...bookingData, serviceId });
    handleNext();
  };

  const handleCategorySelect = async (categoryId: number) => {
    setSelectedCategory(categoryId);
    setSelectedSubcategory(null);
    setSubcategories([]);
    setServices([]);
    setServicesLoading(true);
    setBookingData({ ...bookingData, serviceId: 0 });
    await loadSubcategories(categoryId);
    // Если подкатегорий нет — сразу грузим услуги
    await loadServices(categoryId, null);
  };

  const handleSubcategorySelect = async (subcategoryId: number | null) => {
    setSelectedSubcategory(subcategoryId);
    setServices([]);
    setServicesLoading(true);
    setBookingData({ ...bookingData, serviceId: 0 });
    if (selectedCategory) {
      await loadServices(selectedCategory, subcategoryId);
    }
  };

  const handleMasterSelect = (masterId: number | null) => {
    setBookingData({ ...bookingData, masterId });
    handleNext();
  };

  const handleTimeSelect = (time: string) => {
    const selectedSlot = timeSlots.find(s => s.time === time);
    if (anyMaster && selectedSlot?.master_id) {
      setBookingData({ ...bookingData, time, masterId: selectedSlot.master_id });
    } else {
      setBookingData({ ...bookingData, time });
    }
    handleNext();
  };

  const handleConfirmBooking = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem("token");

      const rawPhone = isAdmin ? bookingData.clientPhone : (currentUserInfo?.phone || "");
      const rawName = isAdmin ? bookingData.clientName : (currentUserInfo?.full_name || "");

      const clientPhone = normalizePhone(rawPhone);
      const clientName = (rawName || "").trim() || "Клиент";

      if (isAdmin) {
        if (!clientPhone) {
          throw new Error("Введите телефон клиента");
        }
        if (clientLookup.status === "not_found" && !((rawName || "").trim())) {
          throw new Error("Введите имя клиента");
        }
      }
      
      // Сначала создаем или получаем клиента
      let clientId: number;

      if (isAdmin && clientLookup.status === "found") {
        clientId = clientLookup.client.id;
      } else {
      const clientByPhoneUrl = `${API_BASE}/clients/by-phone/${encodeURIComponent(clientPhone)}`;

      const clientsResponse = await fetch(clientByPhoneUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (clientsResponse.ok) {
        const existingClient = await clientsResponse.json();
        clientId = existingClient.id;
      } else {
        const clientResponse = await fetch(`${API_BASE}/clients/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: clientName,
            phone: clientPhone,
          }),
        });

        if (clientResponse.ok) {
          const clientData = await clientResponse.json();
          clientId = clientData.id;
        } else {
          let clientErrorText = "";
          try {
            const err = await clientResponse.json();
            clientErrorText = err?.detail ? String(err.detail) : JSON.stringify(err);
          } catch {
            try {
              clientErrorText = await clientResponse.text();
            } catch {
              clientErrorText = "";
            }
          }

          // Если клиент уже существует (400), пробуем получить снова
          const retryResponse = await fetch(clientByPhoneUrl, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (retryResponse.ok) {
            const existingClient = await retryResponse.json();
            clientId = existingClient.id;
          } else {
            throw new Error(`Не удалось создать или найти клиента. Телефон: ${clientPhone}. Ошибка: ${clientResponse.status}${clientErrorText ? ` (${clientErrorText})` : ""}`);
          }
        }
      }
      }
      
      // Создаем запись
      const bookingDateTime = `${bookingData.date.format('YYYY-MM-DD')}T${bookingData.time}:00`;
      const service = services.find(s => s.id === bookingData.serviceId);
      
      if (!service) {
        throw new Error("Услуга не найдена");
      }
      
      const endTime = dayjs(bookingDateTime).add(service?.duration_minutes || 60, 'minute').format('YYYY-MM-DD HH:mm:ss');
      
      const bookingPayload = {
        client_id: clientId,
        master_id: bookingData.masterId || 1, // Если мастер не выбран, используем первого
        service_id: bookingData.serviceId,
        start_time: bookingDateTime,
        end_time: endTime,
        notes: null,
      };
      
      const bookingResponse = await fetch(`${API_BASE}/bookings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(bookingPayload),
      });
      
      if (bookingResponse.ok) {
        setAlert({ show: true, message: "Запись успешно создана!", severity: "success" });
        
        setTimeout(() => {
          navigate("/");
        }, 3000);
      } else {
        const errorData = await bookingResponse.json();
        throw new Error(errorData.detail || "Ошибка создания записи");
      }
      
    } catch (error) {
      console.error("Error confirming booking:", error);
      setAlert({ show: true, message: `Ошибка создания записи: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const getDayName = (date: Dayjs) => {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return dayNames[date.day()];
  };

  const isDateAvailable = (date: Dayjs) => {
    if (date.isBefore(dayjs(), "day")) {
      console.log('Date is in past:', date.format('YYYY-MM-DD'));
      return false;
    }
    
    const location = locations.find(l => l.id === bookingData.locationId);
    console.log('Looking for location with ID:', bookingData.locationId, 'Found locations:', locations.map(l => ({id: l.id, name: l.name})));
    if (!location) {
      console.log('Location not found for ID:', bookingData.locationId);
      return false;
    }
    
    if (!location.work_hours) {
      console.log('Location work_hours not defined');
      return false;
    }
    
    const dayName = getDayName(date);
    const daySchedule = location.work_hours[dayName as keyof typeof location.work_hours];
    
    console.log('Date:', date.format('YYYY-MM-DD'), 'Day:', dayName, 'Schedule:', daySchedule);
    
    return daySchedule?.enabled || false;
  };

  const getDayOccupancy = (date: Dayjs): "low" | "medium" | "high" => {
    // Временно возвращаем низкую загрузку, так как функция асинхронная
    // TODO: Сделать компонент асинхронным или использовать кэширование
    return "low";
  };

  const getCalendarDays = () => {
    // Понедельник-первый, без зависимости от локали dayjs (иначе startOf('week')
    // ведёт себя по-разному и сетка едет на день).
    const startOfMonth = currentMonth.startOf('month');
    const offset = (startOfMonth.day() + 6) % 7; // Пн=0 … Вс=6
    const gridStart = startOfMonth.subtract(offset, 'day');

    const weeks: Dayjs[][] = [];
    for (let w = 0; w < 6; w++) {
      const week: Dayjs[] = [];
      for (let d = 0; d < 7; d++) {
        week.push(gridStart.add(w * 7 + d, 'day'));
      }
      weeks.push(week);
    }

    return weeks;
  };

  const getDayColor = (date: Dayjs) => {
    const isDark = theme.palette.mode === "dark";
    if (!isDateAvailable(date)) return isDark ? theme.palette.action.disabledBackground : "#f5f5f5";

    const occupancy = getDayOccupancy(date);
    if (isDark) {
      switch (occupancy) {
        case "low":
          return theme.palette.grey[800];
        case "medium":
          return "#3a321f";
        case "high":
          return "#3b2026";
        default:
          return theme.palette.grey[800];
      }
    }

    switch (occupancy) {
      case "low":
        return "#f5f5f5";
      case "medium":
        return "#fff3cd";
      case "high":
        return "#f8d7da";
      default:
        return "#f5f5f5";
    }
  };

  const generateTimeSlots = () => {
    // Обёртка: оставляем синхронную сигнатуру, но внутри запускаем async расчёт
    void (async () => {
      const location = locations.find(l => l.id === bookingData.locationId);
      const service = services.find(s => s.id === bookingData.serviceId);

      if (!location || !service || !bookingData.date) {
        setTimeSlots([]);
        return;
      }

      const durationMinutes = Number(service.duration_minutes || 0);
      if (!durationMinutes || durationMinutes < 1) {
        setTimeSlots([]);
        return;
      }

      const dayName = getDayName(bookingData.date);
      const schedule = location.work_hours[dayName as keyof typeof location.work_hours];
      if (!schedule?.enabled) {
        setTimeSlots([]);
        return;
      }

      const dayStr = bookingData.date.format("YYYY-MM-DD");

      const serviceCategoryId = service.category_id;
      const eligibleMasters = masters.filter(m => {
        if (!m.is_active) return false;
        const spec = m.specialization_category_ids || [];
        // если специализации не заданы — считаем мастера универсальным
        if (!spec.length) return true;
        return spec.includes(serviceCategoryId);
      });

      const allMasterIds = eligibleMasters.map(m => m.id);
      const query = new URLSearchParams({ date: `${dayStr}T00:00:00` });
      if (!anyMaster && bookingData.masterId) {
        query.set("master_id", String(bookingData.masterId));
      } else if (anyMaster && allMasterIds.length > 0) {
        query.set("master_ids", allMasterIds.join(","));
      }

      let dayBookings: Array<{
        master_id: number;
        start_time: string;
        end_time: string;
        client?: { name?: string; phone?: string };
        allows_parallel?: boolean;
        apply_minutes?: number;
        finish_minutes?: number;
      }> = [];
      try {
        const token = localStorage.getItem("token");
        const resp = await fetch(`${API_BASE}/bookings/by-date?${query.toString()}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (resp.ok) {
          const data = await resp.json();
          dayBookings = (Array.isArray(data) ? data : []).map((b: any) => ({
            master_id: Number(b.master_id ?? b?.master?.id),
            start_time: String(b.start_time),
            end_time: String(b.end_time),
            client: b?.client ? { name: String(b.client.name || ""), phone: String(b.client.phone || "") } : undefined,
            allows_parallel: Boolean(b?.service?.allows_parallel),
            apply_minutes: Number(b?.service?.apply_minutes || 0),
            finish_minutes: Number(b?.service?.finish_minutes || 0),
          }));
        }
      } catch (e) {
        console.warn("Failed to load bookings for date", e);
      }

      const parseDateTime = (timeHHmm: string) => {
        return dayjs(`${dayStr} ${timeHHmm}`, "YYYY-MM-DD HH:mm");
      };

      const isMasterWorking = (masterId: number, start: dayjs.Dayjs, end: dayjs.Dayjs) => {
        const master = masters.find(m => m.id === masterId);
        const wh = master?.working_hours;
        if (!wh) return true;

        const dayName = getDayName(start);
        const day = wh?.[dayName];
        if (!day) return true;

        if (day.works === false) return false;
        const openStr = day.open || "09:00";
        const closeStr = day.close || "21:00";
        const openT = dayjs(`${dayStr} ${openStr}`, "YYYY-MM-DD HH:mm");
        const closeT = dayjs(`${dayStr} ${closeStr}`, "YYYY-MM-DD HH:mm");
        return !start.isBefore(openT) && !end.isAfter(closeT);
      };

      const intervalOverlaps = (aStart: dayjs.Dayjs, aEnd: dayjs.Dayjs, bStart: dayjs.Dayjs, bEnd: dayjs.Dayjs) => {
        return aStart.isBefore(bEnd) && aEnd.isAfter(bStart);
      };

      // Выбранная услуга — можно ли её вставлять в «окно» другой
      const candidateInsertable = Boolean(service?.is_insertable);

      const isMasterFree = (masterId: number, start: dayjs.Dayjs, end: dayjs.Dayjs) => {
        if (!isMasterWorking(masterId, start, end)) return false;
        const bookingsForMaster = dayBookings.filter(b => b.master_id === masterId);

        for (const b of bookingsForMaster) {
          const bStart = dayjs(b.start_time);
          const bEnd = dayjs(b.end_time);

          let active: Array<[dayjs.Dayjs, dayjs.Dayjs]> = [[bStart, bEnd]];
          let window: [dayjs.Dayjs, dayjs.Dayjs] | null = null;
          if (b.allows_parallel) {
            const ws = bStart.add(b.apply_minutes || 0, "minute");
            const we = bEnd.subtract(b.finish_minutes || 0, "minute");
            if (we.isAfter(ws)) {
              active = [[bStart, ws], [we, bEnd]];
              window = [ws, we];
            }
          }

          // 1) занятые интервалы
          for (const [as_, ae] of active) {
            if (intervalOverlaps(start, end, as_, ae)) return false;
          }
          // 2) попадание в окно — только для вставляемой услуги
          if (window && intervalOverlaps(start, end, window[0], window[1])) {
            if (!candidateInsertable) return false;
            // 3) в окне максимум одна вставленная запись
            for (const b2 of bookingsForMaster) {
              if (b2 === b) continue;
              if (intervalOverlaps(dayjs(b2.start_time), dayjs(b2.end_time), window[0], window[1])) return false;
            }
          }
        }
        return true;
      };

      const open = parseDateTime(schedule.start);
      const close = parseDateTime(schedule.end);
      const stepMinutes = 30;

      const slots: TimeSlot[] = [];
      for (let cursor = open; cursor.isBefore(close); cursor = cursor.add(stepMinutes, "minute")) {
        const start = cursor;
        const end = cursor.add(durationMinutes, "minute");

        const bookedStart = dayBookings.find(b => {
          const bStart = dayjs(b.start_time);
          if (bStart.format("HH:mm") !== start.format("HH:mm")) return false;

          if (!anyMaster) {
            return Number(b.master_id) === Number(bookingData.masterId);
          }
          return allMasterIds.includes(Number(b.master_id));
        });

        const isBookedStart = Boolean(bookedStart);
        const bookedClient = bookedStart?.client?.name
          ? { name: String(bookedStart.client.name), phone: String(bookedStart.client.phone || "") }
          : undefined;

        // слот должен целиком помещаться до закрытия
        if (end.isAfter(close)) {
          slots.push({ time: start.format("HH:mm"), available: false, isBookedStart, bookedClient });
          continue;
        }

        if (!anyMaster) {
          const selectedMasterId = bookingData.masterId;
          if (!selectedMasterId) {
            slots.push({ time: start.format("HH:mm"), available: false, isBookedStart, bookedClient });
            continue;
          }
          const free = isMasterFree(selectedMasterId, start, end);
          slots.push({
            time: start.format("HH:mm"),
            available: free,
            isBookedStart: !free ? isBookedStart : false,
            bookedClient: !free ? bookedClient : undefined,
          });
          continue;
        }

        // any master: найдём первого свободного
        let foundMasterId: number | undefined;
        for (const mid of allMasterIds) {
          if (isMasterFree(mid, start, end)) {
            foundMasterId = mid;
            break;
          }
        }

        slots.push({
          time: start.format("HH:mm"),
          available: Boolean(foundMasterId),
          isBookedStart: !foundMasterId ? isBookedStart : false,
          bookedClient: !foundMasterId ? bookedClient : undefined,
          ...(foundMasterId ? { master_id: foundMasterId } : {}),
        });
      }

      // Доп. слоты — точное начало «окна» для вставляемой услуги (вне сетки :00/:30)
      if (candidateInsertable) {
        const relevantMasters = anyMaster
          ? allMasterIds
          : (bookingData.masterId ? [Number(bookingData.masterId)] : []);
        const seen = new Set(slots.map((s) => s.time));
        for (const b of dayBookings) {
          if (!b.allows_parallel) continue;
          if (!relevantMasters.includes(b.master_id)) continue;
          const start = dayjs(b.start_time).add(b.apply_minutes || 0, "minute");
          const hhmm = start.format("HH:mm");
          if (seen.has(hhmm)) continue;
          const end = start.add(durationMinutes, "minute");
          if (end.isAfter(close)) continue;

          let freeMaster: number | undefined;
          if (!anyMaster) {
            if (bookingData.masterId && isMasterFree(Number(bookingData.masterId), start, end)) {
              freeMaster = Number(bookingData.masterId);
            }
          } else {
            for (const mid of allMasterIds) {
              if (isMasterFree(mid, start, end)) { freeMaster = mid; break; }
            }
          }
          if (freeMaster) {
            seen.add(hhmm);
            slots.push({
              time: hhmm,
              available: true,
              isBookedStart: false,
              bookedClient: undefined,
              ...(anyMaster ? { master_id: freeMaster } : {}),
            });
          }
        }
        slots.sort((a, b) => a.time.localeCompare(b.time));
      }

      setTimeSlots(slots);
    })();
  };

  useEffect(() => {
    const onBookingChange = () => {
      setSlotsRefreshVersion(v => v + 1);
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key === "booking_change") {
        onBookingChange();
      }
    };

    window.addEventListener("booking_change", onBookingChange as EventListener);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("booking_change", onBookingChange as EventListener);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    const timeStepIndex = shouldSkipLocationStep ? 3 : 4;
    if (activeStep === timeStepIndex && bookingData.date && bookingData.serviceId) {
      generateTimeSlots();
    }
  }, [
    activeStep,
    bookingData.date,
    bookingData.serviceId,
    bookingData.masterId,
    anyMaster,
    masters,
    locations,
    services,
    shouldSkipLocationStep,
    slotsRefreshVersion,
  ]);

  if (loading && locations.length === 0) {
    return (
      <Box sx={{ width: "100%" }}>
        <Header title="Онлайн-запись" />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: "background.default" }}>
      <Header title="Онлайн-запись" />
      
      <Box sx={{ p: { xs: 1, sm: 3 }, maxWidth: 800, mx: "auto" }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link color="inherit" href="#" onClick={() => navigate("/")}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Главная
          </Link>
          <Typography color="text.primary">Онлайн-запись</Typography>
        </Breadcrumbs>

        {/* Шаг 0: Выбор места (только если несколько мест) */}
        {!shouldSkipLocationStep && activeStep === 0 && (
          <Box>
            <Typography variant="h5" gutterBottom>
              Выберите салон
            </Typography>
            <Grid container spacing={2}>
              {locations.map((location) => (
                <Grid item xs={12} md={6} key={location.id}>
                  <Card 
                    sx={{ 
                      cursor: "pointer", 
                      "&:hover": { boxShadow: 4 },
                      border: bookingData.locationId === location.id ? 2 : 1,
                      borderColor: bookingData.locationId === location.id ? "primary.main" : "grey.300"
                    }}
                    onClick={() => handleLocationSelect(location.id)}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={2}>
                        <LocationIcon color="primary" />
                        <Box>
                          <Typography variant="h6">{location.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {location.address}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Шаг 1: Выбор даты */}
        {((shouldSkipLocationStep && activeStep === 0) || (!shouldSkipLocationStep && activeStep === 1)) && (
          <Box>
            <Typography variant="h5" gutterBottom>
              Выберите дату
            </Typography>
            <Paper sx={{ p: { xs: 1, sm: 3 } }}>
              {/* Навигация по месяцам */}
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <IconButton onClick={() => setCurrentMonth(currentMonth.subtract(1, 'month'))}>
                  <PrevMonthIcon />
                </IconButton>
                <Typography variant="h6">
                  {currentMonth.format("MMMM YYYY")}
                </Typography>
                <IconButton onClick={() => setCurrentMonth(currentMonth.add(1, 'month'))}>
                  <NextMonthIcon />
                </IconButton>
              </Box>

              {/* Дни недели */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                  mb: 1,
                  gap: { xs: 0.5, sm: 1 },
                }}
              >
                {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((day) => (
                  <Box
                    key={day}
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    {day}
                  </Box>
                ))}
              </Box>

              {/* Календарь */}
              <Box sx={{ width: "100%", overflowX: "hidden" }}>
                {(() => {
                  const calendarWeeks = getCalendarDays();
                  console.log('Calendar weeks:', calendarWeeks); // Отладка
                  return calendarWeeks.map((week, weekIndex) => (
                    <Box
                      key={weekIndex}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                        gap: { xs: 0.5, sm: 1 },
                        mb: { xs: 0.5, sm: 1 },
                      }}
                    >
                      {week.map((day, dayIndex) => {
                        if (!day) {
                          return (
                            <Box
                              key={dayIndex}
                              sx={{
                                width: "100%",
                                aspectRatio: "1 / 1",
                              }}
                            />
                          );
                        }
                        
                        const isCurrentMonth = day.month() === currentMonth.month();
                        const isSelected = bookingData.date.isSame(day, 'day');
                        const isAvailable = isDateAvailable(day);
                        
                        return (
                          <Box
                            key={dayIndex}
                            sx={{
                              width: "100%",
                              minWidth: 0,
                              textAlign: 'center',
                            }}
                          >
                            <Card
                              sx={{
                                cursor: isAvailable ? 'pointer' : 'not-allowed',
                                opacity: !isAvailable ? 0.4 : (isCurrentMonth ? 1 : 0.3),
                                backgroundColor: isSelected ? 'primary.main' : getDayColor(day),
                                color: isSelected ? 'white' : 'inherit',
                                border: isSelected ? 2 : 1,
                                borderColor: isSelected ? 'primary.main' : 'divider',
                                "&:hover": isAvailable ? { boxShadow: 2 } : {},
                                width: "100%",
                                minWidth: 0,
                                aspectRatio: "1 / 1",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              onClick={() => isAvailable && handleDateSelect(day)}
                            >
                              <CardContent sx={{ p: { xs: 0.5, sm: 1 } }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: isSelected ? "bold" : "normal",
                                    fontSize: { xs: "0.875rem", sm: "1rem" },
                                  }}
                                >
                                  {day.format('D')}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Box>
                        );
                      })}
                    </Box>
                  ));
                })()}
              </Box>

              {/* Легенда */}
              <Box mt={3} display="flex" gap={2} flexWrap="wrap" justifyContent="center">
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : "#f5f5f5",
                      border: 1,
                      borderColor: theme.palette.mode === "dark" ? "grey.700" : "grey.300",
                    }}
                  />
                  <Typography variant="caption">Много мест</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      backgroundColor: theme.palette.mode === "dark" ? "#3a321f" : "#fff3cd",
                      border: 1,
                      borderColor: theme.palette.mode === "dark" ? "grey.700" : "grey.300",
                    }}
                  />
                  <Typography variant="caption">~50% занято</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      backgroundColor: theme.palette.mode === "dark" ? "#3b2026" : "#f8d7da",
                      border: 1,
                      borderColor: theme.palette.mode === "dark" ? "grey.700" : "grey.300",
                    }}
                  />
                  <Typography variant="caption">Мало мест</Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        )}

        {/* Шаг 2: Выбор услуги */}
        {((shouldSkipLocationStep && activeStep === 1) || (!shouldSkipLocationStep && activeStep === 2)) && (
          <Box>
            <Typography variant="h5" gutterBottom>
              Выберите услугу
            </Typography>
            
            {/* Проверка загрузки данных */}
            {loading && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Загрузка услуг...</Typography>
              </Box>
            )}
            
            {/* Если данных нет */}
            {!loading && categories.length === 0 && (
              <Alert severity="warning">
                Услуги не найдены. Пожалуйста, вернитесь и попробуйте снова.
              </Alert>
            )}
            
            {/* Если данные есть */}
            {!loading && categories.length > 0 && (
              <>
                {selectedCategory === null && (
                  <Grid container spacing={2}>
                    {categories.map((category) => (
                      <Grid item xs={12} sm={6} key={category.id}>
                        <Card
                          sx={{ cursor: "pointer", "&:hover": { boxShadow: 4 } }}
                          onClick={() => void handleCategorySelect(category.id)}
                        >
                          <CardContent>
                            <Typography variant="h6">{category.name}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {selectedCategory !== null && (
                  <Box>
                    <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Chip label="Назад к категориям" onClick={() => {
                        setSelectedCategory(null);
                        setSelectedSubcategory(null);
                        setSubcategories([]);
                        setServices([]);
                        setBookingData({ ...bookingData, serviceId: 0 });
                      }} />

                      {subcategories.length > 0 && (
                        <Chip
                          label="Все"
                          color={selectedSubcategory === null ? "primary" : "default"}
                          onClick={() => void handleSubcategorySelect(null)}
                        />
                      )}
                    </Box>

                    {subcategories.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Подкатегория
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {subcategories.map((sc) => (
                            <Chip
                              key={sc.id}
                              label={sc.name}
                              color={selectedSubcategory === sc.id ? "primary" : "default"}
                              onClick={() => void handleSubcategorySelect(sc.id)}
                              sx={{ height: isMobile ? 40 : undefined, fontSize: isMobile ? "1rem" : undefined }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {servicesLoading ? (
                      <Box display="flex" alignItems="center" justifyContent="center" minHeight={120}>
                        <CircularProgress size={24} />
                        <Typography sx={{ ml: 2 }}>Загрузка услуг...</Typography>
                      </Box>
                    ) : services.length === 0 ? (
                      <Alert severity="warning">Услуги не найдены.</Alert>
                    ) : (
                      <Grid container spacing={2}>
                        {services.map((service) => (
                          <Grid item xs={12} md={6} key={service.id}>
                            <Card
                              sx={{
                                cursor: "pointer",
                                "&:hover": { boxShadow: 4 },
                                border: bookingData.serviceId === service.id ? 2 : 1,
                                borderColor: bookingData.serviceId === service.id ? "primary.main" : "grey.300",
                              }}
                              onClick={() => handleServiceSelect(service.id)}
                            >
                              <CardContent>
                                <Box display="flex" alignItems="center" gap={2}>
                                  <ServiceIcon color="primary" />
                                  <Box flex={1}>
                                    <Typography variant="h6">{service.name}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {service.duration_minutes} мин • от {service.base_price} ₽
                                    </Typography>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* Шаг 3: Выбор мастера */}
        {((shouldSkipLocationStep && activeStep === 2) || (!shouldSkipLocationStep && activeStep === 3)) && (
          <Box>
            <Typography variant="h5" gutterBottom>
              Выберите мастера
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={anyMaster}
                    onChange={(e) => {
                      setAnyMaster(e.target.checked);
                      if (e.target.checked) {
                        handleMasterSelect(null);
                      }
                    }}
                  />
                }
                label="Любой доступный мастер"
              />
            </Box>

            {!anyMaster && (
              <Grid container spacing={2}>
                {masters
                  .filter(master => {
                    // Показываем только активных мастеров
                    if (!master.is_active) return false;

                    const service = services.find(s => s.id === bookingData.serviceId);
                    if (!service) return true;

                    const spec = master.specialization_category_ids || [];
                    if (!spec.length) return true;
                    return spec.includes(service.category_id);
                  })
                  .map((master) => (
                  <Grid item xs={12} md={6} key={master.id}>
                    <Card 
                      sx={{ 
                        cursor: "pointer", 
                        "&:hover": { boxShadow: 4 },
                        border: bookingData.masterId === master.id ? 2 : 1,
                        borderColor: bookingData.masterId === master.id ? "primary.main" : "grey.300"
                      }}
                      onClick={() => handleMasterSelect(master.id)}
                    >
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar
                            src={master.photo_url}
                            sx={{ width: 60, height: 60 }}
                          >
                            <MasterIcon />
                          </Avatar>
                          <Box flex={1}>
                            <Typography variant="h6">{master.full_name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {master.role}
                            </Typography>
                            {master.rating && (
                              <Typography variant="caption" color="primary">
                                Рейтинг: {master.rating} ⭐
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* Шаг 4: Выбор времени */}
        {((shouldSkipLocationStep && activeStep === 3) || (!shouldSkipLocationStep && activeStep === 4)) && (
          <Box>
            <Typography variant="h5" gutterBottom>
              Выберите время
            </Typography>
            <Paper sx={{ p: 2 }}>
              <Box display="flex" gap={1} flexWrap="wrap">
                {timeSlots.map((slot, index) => (
                  <Chip
                    key={index}
                    label={slot.time}
                    onClick={() => {
                      if (slot.available) {
                        handleTimeSelect(slot.time);
                        return;
                      }
                      if (slot.isBookedStart && slot.bookedClient?.name) {
                        setOccupiedInfo({
                          time: slot.time,
                          name: slot.bookedClient.name,
                          phone: slot.bookedClient.phone,
                        });
                        setOccupiedInfoOpen(true);
                      }
                    }}
                    disabled={!slot.available && !slot.isBookedStart}
                    color={bookingData.time === slot.time ? "primary" : "default"}
                    variant={bookingData.time === slot.time ? "filled" : "outlined"}
                    sx={{
                      m: 0.5,
                      height: isMobile ? 40 : undefined,
                      fontSize: isMobile ? "1rem" : undefined,
                      cursor: slot.available || slot.isBookedStart ? "pointer" : "not-allowed",
                      ...(slot.available
                        ? bookingData.time === slot.time
                          ? {}
                          : {
                              bgcolor: "#e7f6ec",
                              borderColor: "#43a047",
                              color: "#2e7d32",
                              fontWeight: 600,
                              "&:hover": { bgcolor: "#d6f0df", borderColor: "#2e7d32" },
                            }
                        : slot.isBookedStart
                        ? {
                            bgcolor: "action.selected",
                            borderColor: "divider",
                            color: "text.secondary",
                            fontWeight: 600,
                            opacity: 0.9,
                            "&.Mui-disabled": { color: "text.secondary", opacity: 0.9 },
                          }
                        : {
                            bgcolor: "transparent",
                            borderColor: "divider",
                            color: "text.disabled",
                            opacity: 0.4,
                            textDecoration: "line-through",
                            "&.Mui-disabled": {
                              color: "text.disabled",
                              opacity: 0.4,
                            },
                          }),
                    }}
                  />
                ))}
              </Box>
            </Paper>

            <Dialog open={occupiedInfoOpen} onClose={() => setOccupiedInfoOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle>Запись занята</DialogTitle>
              <DialogContent>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Время: {occupiedInfo?.time}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Клиент: {occupiedInfo?.name}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Телефон: {occupiedInfo?.phone}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOccupiedInfoOpen(false)}>Закрыть</Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}

        {/* Шаг 5: Подтверждение */}
        {((shouldSkipLocationStep && activeStep === 4) || (!shouldSkipLocationStep && activeStep === 5)) && (
          <Box>
            <Typography variant="h5" gutterBottom>
              Подтверждение записи
            </Typography>
            
            {/* Сводка записи */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Детали записи
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Место
                    </Typography>
                    <Typography variant="body1">
                      {locations.find(l => l.id === bookingData.locationId)?.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Дата и время
                    </Typography>
                    <Typography variant="body1">
                      {bookingData.date.format("DD MMMM YYYY")} в {bookingData.time}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Услуга
                    </Typography>
                    <Typography variant="body1">
                      {services.find(s => s.id === bookingData.serviceId)?.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Мастер
                    </Typography>
                    <Typography variant="body1">
                      {anyMaster ? "Любой доступный мастер" : 
                       masters.find(m => m.id === bookingData.masterId)?.full_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Стоимость
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {services.find(s => s.id === bookingData.serviceId)?.base_price} ₽
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {isAdmin && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Данные клиента
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Телефон"
                        value={bookingData.clientPhone}
                        onChange={(e) => setBookingData({ ...bookingData, clientPhone: e.target.value })}
                        onBlur={() => {
                          const normalized = normalizePhone(bookingData.clientPhone);
                          if (normalized && normalized !== bookingData.clientPhone) {
                            setBookingData((prev) => ({ ...prev, clientPhone: normalized }));
                          }
                        }}
                        required
                        helperText={
                          clientLookup.status === "loading"
                            ? "Поиск клиента..."
                            : clientLookup.status === "found"
                              ? `Найден клиент: ${clientLookup.client.name}`
                              : clientLookup.status === "not_found"
                                ? "Клиент не найден — будет создан новый"
                                : clientLookup.status === "error"
                                  ? (clientLookup.message || "Ошибка поиска клиента")
                                  : ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Имя"
                        value={bookingData.clientName}
                        onChange={(e) => setBookingData({ ...bookingData, clientName: e.target.value })}
                        required={clientLookup.status !== "found"}
                        disabled={clientLookup.status === "found"}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
              <Button onClick={handleBack} variant="outlined">
                Назад
              </Button>
              <Button
                onClick={handleConfirmBooking}
                variant="contained"
                disabled={
                  isAdmin
                    ? !bookingData.clientPhone || (clientLookup.status === "not_found" && !bookingData.clientName)
                    : false
                }
                startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
              >
                {loading ? "Создание записи..." : "Подтверждаю"}
              </Button>
            </Box>
          </Box>
        )}

        {/* Кнопка "Назад" для всех шагов кроме первого */}
        {activeStep > 0 && (
          <Box sx={{ mt: 3 }}>
            <Button onClick={handleBack} variant="outlined" startIcon={<ArrowBackIcon />}>
              Назад
            </Button>
          </Box>
        )}
      </Box>

      {/* Snackbar для уведомлений */}
      <Snackbar
        open={alert.show}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, show: false })}
      >
        <Alert
          onClose={() => setAlert({ ...alert, show: false })}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
